import React, { useState, useEffect } from 'react';
import {
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink
} from 'reactstrap';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { history, store } from '../../../../store/store';
import { UserDetailsTab } from './tabs/UserDetailsTab';
import { AdminEditForm } from './forms/edit/AdminEditForm';
import { UserGroupsTab } from './tabs/UserGroupsTab';
import { ACL } from '../../../../config/constants';
import { hideNotification } from '../../../../store/actions/notification';
import { NOTIFICATION_ERROR } from '../../../../store/actions/types';

function UserDetailsComponent(props) {
  const {
    user,
    updateUser,
    groupsUserIsIn,
    groupsUserIsNotIn,
    updateGroups,
    submit
  } = props;
  const [ activeTab, setActiveTab ] = useState( 'user' );
  const [ tabs, setTabs ] = useState( { user: false } );
  const intl = useIntl();
  const hasGroupsTabAccess = ACL.ADMIN.USERS.VIEW.hasAccess() &&
    ACL.ADMIN.GROUPS.LIST.hasAccess();
  const focusableTab = activeTab === 'user' || (activeTab === 'groups' && hasGroupsTabAccess) ?
    activeTab : 'user';

  useEffect( () => {
    const tab = new URLSearchParams( props.location.search ).get( 'tab' );

    if (tab) {
      if (activeTab !== tab) {
        setActiveTab( tab );
      } else {
        resetEditMode();
      }
    }

    return () => {
      if (store.getState().notification.type === NOTIFICATION_ERROR) {
        store.dispatch(hideNotification());
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [] );

  const toggle = (tab) => {
    if (activeTab !== tab) {
      history.push( {
        search: '?' + new URLSearchParams( { tab: tab } ).toString(),
      } );

      store.dispatch(hideNotification());
      setActiveTab( tab );
    }
  };

  const resetEditMode = () => {
    const tabsU = tabs;
    let shouldUpdate = false;

    Object.keys( tabsU ).map( (key) => {
      if (activeTab !== key && tabsU[key] === true) {
        tabsU[key] = false;
        shouldUpdate = true;
      }

      return true;
    } );

    if (shouldUpdate) {
      setTabs( tabsU );
    }
  };

  const toggleEditMode = (target, value) => {
    setTabs( {
      ...tabs,
      [target]: value,
    } );
  };

  const handleTabListKeyDown = (event) => {
    const tabButtons = Array.from(
      event.currentTarget.querySelectorAll('[role="tab"]:not(:disabled)')
    );
    const currentIndex = tabButtons.indexOf(document.activeElement);
    let nextIndex;

    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabButtons.length - 1;
    } else if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabButtons.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabButtons.length) % tabButtons.length;
    } else {
      return;
    }

    event.preventDefault();
    tabButtons[nextIndex].focus();
  };

  return (
    <div className="admin-user-detail-tabs">
      <Nav
        aria-label={intl.formatMessage({ id: 'pages.admin.view_user.title' })}
        className="admin-user-detail-tabs__nav"
        onKeyDown={handleTabListKeyDown}
        role="tablist"
        tabs
      >
        <NavItem role="presentation">
          <NavLink
            aria-controls="admin-user-detail-panel-user"
            aria-selected={activeTab === 'user'}
            className={classnames( {
              active: activeTab === 'user',
            } )}
            id="admin-user-detail-tab-user"
            onClick={() => {
              toggle( 'user' );
            }}
            role="tab"
            tabIndex={focusableTab === 'user' ? 0 : -1}
            tag="button"
            type="button"
          >
            <FormattedMessage id="components.pages.admin.users.user_details.title"/>
          </NavLink>
        </NavItem>
        {hasGroupsTabAccess && <NavItem role="presentation">
          <NavLink
            aria-controls="admin-user-detail-panel-groups"
            aria-selected={activeTab === 'groups'}
            className={classnames( {
              active: activeTab === 'groups',
            } )}
            id="admin-user-detail-tab-groups"
            onClick={() => {
              toggle( 'groups' );
            }}
            role="tab"
            tabIndex={focusableTab === 'groups' ? 0 : -1}
            tag="button"
            type="button"
          >
            <FormattedMessage id="components.pages.admin.user_manage_groups"/>
          </NavLink>
        </NavItem>}
      </Nav>
      <TabContent activeTab={activeTab} className="admin-user-detail-tabs__content">
        <TabPane
          aria-hidden={activeTab !== 'user'}
          aria-labelledby="admin-user-detail-tab-user"
          className="admin-user-detail-tabs__panel admin-user-detail-tabs__panel--details"
          id="admin-user-detail-panel-user"
          role="tabpanel"
          tabId="user"
          tabIndex="0"
        >
          {
            tabs.user ? (
              <AdminEditForm
                user={user}
                toggleEditMode={toggleEditMode}
                updateUser={updateUser}
              />
            ) : (
              <UserDetailsTab user={user} toggleEditMode={toggleEditMode}/>
            )}
        </TabPane>
        {hasGroupsTabAccess && <TabPane
          aria-hidden={activeTab !== 'groups'}
          aria-labelledby="admin-user-detail-tab-groups"
          className="admin-user-detail-tabs__panel admin-user-detail-tabs__panel--groups"
          id="admin-user-detail-panel-groups"
          role="tabpanel"
          tabId="groups"
          tabIndex="0"
        >
          <UserGroupsTab
            user={user}
            groupsUserIsIn={groupsUserIsIn}
            groupsUserIsNotIn={groupsUserIsNotIn}
            updateGroups={updateGroups}
            submit={submit}
          />
        </TabPane>}
      </TabContent>
    </div>
  );
}

UserDetailsComponent.defaultProps = {};

const UserDetails = connect( null, {} )( UserDetailsComponent );

export { UserDetails, UserDetailsComponent };
